import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Copy,
  Minus,
  RefreshCw,
  Share2,
  X,
} from "lucide-react";
import type { LegalRiskResult, ToolId } from "../lib/readerTools";
import {
  toolPageDescription,
  toolPageKicker,
} from "../lib/toolVisuals";
import { legalRiskToMarkdown, markdownToHtml } from "../lib/markdown";
import { copyText, shareText } from "../lib/shareText";

type ShellProps = {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  onMinimize?: () => void;
  copyTextValue?: string;
  onShare?: () => void;
  shareDisabled?: boolean;
  footer?: React.ReactNode;
  busy?: boolean;
  /** Tool id → kicker / default description / optional hero. */
  toolId?: ToolId;
  /** Short header description; defaults from toolId when omitted. */
  description?: string | null;
  /** Pill above the title; defaults from toolId. Pass null to hide. */
  kicker?: string | null;
  /**
   * Hero illustration under the lead. Pass a src to show, or omit/null to hide.
   * Scan page should pass the scan-hero asset for the empty capture state.
   */
  heroSrc?: string | null;
  /**
   * RTL: text-back at the leading edge instead of X.
   * Default true — matches restored scan flow-page chrome.
   */
  closeAsBack?: boolean;
  /** Fill parent (e.g. dashboard scan) instead of fixed fullscreen overlay. */
  embedded?: boolean;
  /** Extra class on the flow-page root (e.g. scan-page). */
  pageClassName?: string;
};

/**
 * Shared tool chrome based on the restored scan / consumer flow-page design:
 * back + kicker + title + lead (+ optional hero), scrollable body, sticky bottom actions.
 */
export function ToolPageShell({
  title,
  children,
  onClose,
  onMinimize,
  copyTextValue,
  onShare,
  shareDisabled,
  footer,
  busy,
  toolId,
  description,
  kicker,
  heroSrc,
  closeAsBack = true,
  embedded = false,
  pageClassName,
}: ShellProps) {
  const [hint, setHint] = useState<string | null>(null);
  const canCopy = Boolean(copyTextValue?.trim());
  const resolvedDescription =
    description === null
      ? null
      : description ?? (toolId ? toolPageDescription(toolId) : null);
  const resolvedKicker =
    kicker === null
      ? null
      : kicker ?? (toolId ? toolPageKicker(toolId) : null);
  const resolvedHero = heroSrc ?? null;

  const pageClasses = [
    "flow-page",
    "consumer-shell",
    "tool-flow-page",
    embedded ? "embedded" : "fullscreen",
    pageClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const panel = (
    <div
      className={pageClasses}
      role="dialog"
      aria-label={title}
      onClick={(e) => e.stopPropagation()}
    >
      <header className="flow-page-head">
        <div className="tool-flow-head-top">
          {closeAsBack ? (
            <button type="button" className="text-back" onClick={onClose}>
              <ArrowRight size={16} />
              بازگشت
            </button>
          ) : (
            <span className="tool-flow-head-spacer" />
          )}
          <div className="tool-flow-head-actions">
            {canCopy && (
              <button
                type="button"
                className="icon-btn compact"
                aria-label="کپی"
                title="کپی"
                disabled={busy}
                onClick={() => {
                  void copyText(copyTextValue!)
                    .then(() => setHint("کپی شد"))
                    .catch((e) =>
                      setHint(e instanceof Error ? e.message : "خطا در کپی"),
                    );
                }}
              >
                <Copy size={15} />
              </button>
            )}
            {onShare && (
              <button
                type="button"
                className="icon-btn compact"
                aria-label="اشتراک"
                title="اشتراک"
                disabled={shareDisabled || busy}
                onClick={onShare}
              >
                <Share2 size={15} />
              </button>
            )}
            {onMinimize && (
              <button
                type="button"
                className="icon-btn compact"
                aria-label="کوچک‌سازی"
                title="کوچک‌سازی"
                onClick={onMinimize}
              >
                <Minus size={15} />
              </button>
            )}
            {!closeAsBack && (
              <button
                type="button"
                className="icon-btn compact"
                aria-label="بستن"
                onClick={onClose}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
        {resolvedKicker ? (
          <span className="consumer-kicker">{resolvedKicker}</span>
        ) : null}
        <h1>{title}</h1>
        {resolvedDescription ? <p>{resolvedDescription}</p> : null}
        {resolvedHero ? (
          <div className="hero-art">
            <img src={resolvedHero} alt="" decoding="async" />
          </div>
        ) : null}
      </header>

      <div className="tool-flow-body">
        {children}
        {hint && <p className="settings-hint">{hint}</p>}
      </div>

      {footer ? (
        <div className="sheet-actions sticky-actions tool-flow-actions">
          {footer}
        </div>
      ) : null}
    </div>
  );

  if (embedded) return panel;

  return (
    <div className="tool-flow-backdrop fullscreen" role="presentation">
      {panel}
    </div>
  );
}

export function MarkdownBody({ text }: { text: string }) {
  const html = useMemo(() => markdownToHtml(text), [text]);
  if (!text.trim()) return <p className="settings-hint">محتوایی نیست.</p>;
  return (
    <div className="tool-md" dangerouslySetInnerHTML={{ __html: html }} />
  );
}

type SummaryProps = {
  title: string;
  text: string;
  history: string[];
  busy?: boolean;
  error?: string | null;
  regenerating?: boolean;
  onRegenerate: () => void;
  onClose: () => void;
  onMinimize?: () => void;
  onPickHistory?: (idx: number) => void;
};

export function SummaryToolPage({
  title,
  text,
  history,
  busy,
  error,
  regenerating,
  onRegenerate,
  onClose,
  onMinimize,
  onPickHistory,
}: SummaryProps) {
  const showProgress = Boolean(busy || regenerating);
  const archived = history.slice(text && history[0] === text ? 1 : 0);

  return (
    <ToolPageShell
      title={title}
      toolId="summary"
      onClose={onClose}
      onMinimize={onMinimize}
      copyTextValue={text}
      busy={busy}
      onShare={
        text
          ? () => {
              void shareText(text, "nevisign-summary.txt");
            }
          : undefined
      }
      shareDisabled={!text}
      footer={
        <button
          type="button"
          className="primary-btn"
          disabled={busy}
          onClick={onRegenerate}
        >
          <RefreshCw size={16} /> تولید مجدد
        </button>
      }
    >
      {showProgress && (
        <div className="summary-version current in-progress" role="status">
          <div className="summary-version-badge">نسخه جدید · در حال تولید…</div>
          <p className="settings-hint">
            خلاصهٔ تازه بالای نسخه‌های قبلی قرار می‌گیرد.
          </p>
        </div>
      )}
      {text && !showProgress && (
        <div className="summary-version current">
          <div className="summary-version-badge">نسخه فعلی</div>
          <MarkdownBody text={text} />
        </div>
      )}
      {!text && !showProgress && (
        <p className="settings-hint">خلاصه‌ای موجود نیست.</p>
      )}
      {error && <p className="settings-hint">{error}</p>}
      {(archived.length > 0 || (showProgress && text)) && (
        <div className="summary-history">
          <p className="settings-hint">
            {showProgress && text
              ? "نسخهٔ قبلی به پایین منتقل شد"
              : "نسخه‌های قبلی"}
          </p>
          {(showProgress && text ? [text, ...archived] : archived).map(
            (item, i) => (
              <button
                key={`h-${i}-${item.slice(0, 12)}`}
                type="button"
                className="summary-version archived"
                onClick={() => {
                  if (showProgress && text && i === 0) return;
                  const histIdx =
                    showProgress && text ? i : history.indexOf(item);
                  onPickHistory?.(histIdx >= 0 ? histIdx : i);
                }}
              >
                <div className="summary-version-badge">
                  نسخه{" "}
                  {(showProgress && text
                    ? archived.length + 1 - i
                    : archived.length - i
                  ).toLocaleString("fa-IR")}
                </div>
                <div className="summary-version-preview">
                  {item.slice(0, 180)}
                  {item.length > 180 ? "…" : ""}
                </div>
              </button>
            ),
          )}
        </div>
      )}
    </ToolPageShell>
  );
}

type RiskProps = {
  result: LegalRiskResult | null;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onMinimize?: () => void;
  onShare: () => void;
};

export function LegalRiskToolPage({
  result,
  busy,
  error,
  onClose,
  onMinimize,
  onShare,
}: RiskProps) {
  const md = useMemo(
    () => (result ? legalRiskToMarkdown(result) : ""),
    [result],
  );

  return (
    <ToolPageShell
      title="تحلیل ریسک حقوقی"
      toolId="legal-risk"
      onClose={onClose}
      onMinimize={onMinimize}
      copyTextValue={md}
      onShare={onShare}
      shareDisabled={!result}
      busy={busy}
    >
      {busy && !result ? (
        <p className="settings-hint">در حال تحلیل…</p>
      ) : (
        <>
          {md ? <MarkdownBody text={md} /> : !busy && <p>نتیجه‌ای نیست.</p>}
          {error && <p className="settings-hint">{error}</p>}
        </>
      )}
    </ToolPageShell>
  );
}

type ToastProps = { message: string; onDone: () => void };

export function RevToast({ message, onDone }: ToastProps) {
  useEffect(() => {
    const t = window.setTimeout(onDone, 2800);
    return () => window.clearTimeout(t);
  }, [message, onDone]);
  return (
    <div className="rev-toast" role="status">
      {message}
    </div>
  );
}

/** Small reopen chip shown above the AI FAB when a tool is minimized. */
export function MinimizedToolChip({
  label,
  onOpen,
}: {
  label: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className="tool-min-chip"
      aria-label={`بازگشت به ${label}`}
      title={label}
      onClick={onOpen}
    >
      <span className="tool-min-chip-dot" aria-hidden />
      {label}
    </button>
  );
}
